'use client';

import Image from 'next/image';
import { useRef, Fragment } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import ChipText from '@/components/global/ChipText';
import {GROQImage} from "@/sanity/lib/types";
import { CTAButton } from '@/shadcn/ui/button';
import { GROQButton } from '@/sanity/lib/definitions';

interface IProcessProps {
  chipText: string;
  title: PortableTextBlock[];
  items: {
    image: GROQImage;
    imageMobile: GROQImage;
    text: PortableTextBlock[];
  }[];
  buttonOne: GROQButton;
  buttonTwo: GROQButton;
}

export default function Process({
  chipText,
  title,
  items,
  buttonOne,
  buttonTwo,
}: IProcessProps) {
  const isMobile = useIsMobile();

  return (
    <section className="flex w-full items-center justify-center bg-[#072922] pt-[10rem] pb-[12rem] lg:pt-[16rem] lg:pb-[30.75rem]">
      <div className="container flex flex-col items-center justify-center gap-[6rem] lg:gap-[10rem] w-full relative">
        <div className="flex flex-col items-center justify-center gap-[2rem] lg:pt-[2rem]">
          {chipText &&
            <ChipText text={chipText} color="purple" className="!text-[#B3A3EE] lg:text-[2rem] !font-[200]" />
          }
          <div className="richtext richtext-center flex flex-col gap-[3rem] prose-headings:text-offwhite prose-headings:max-w-[42rem] lg:prose-headings:max-w-none prose-p:max-w-[88rem] prose-p:font-[300] lg:prose-p:text-[2.25rem] prose-p:tracking-[-0.0375rem] prose-p:leading-[160%] prose-p:text-offwhite/50">
            <PortableText value={title} components={richTextComponent} />
          </div>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-[6rem] lg:gap-[3rem] max-w-[207.75rem] w-full">
          {items?.length > 0 && items?.map((item, index) => (
            <div key={index} className="flex flex-col gap-[3rem] lg:gap-[4rem] w-full lg:min-h-[58rem]">
              <Image
                className="!w-full !h-auto"
                src={ isMobile ? item.imageMobile.src : item.image.src}
                width={ isMobile ? item.imageMobile.width : item.image.width}
                height={ isMobile ? item.imageMobile.height : item.image.height}
                quality={100}
                alt=""
              />
              <div className="richtext richtext-start flex flex-col gap-[1.75rem] lg:gap-[2.35rem] prose-headings:text-offwhite prose-headings:text-[3rem] lg:prose-headings:text-[4rem] prose-p:max-w-[57rem] prose-p:font-[300] prose-p:text-[2rem] prose-p:tracking-[-0.02rem] prose-p:text-offwhite/50">
                <PortableText value={item.text} components={richTextComponent} />
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-[1rem] lg:gap-[3rem] w-full lg:w-auto">
          <CTAButton data={buttonOne} className="w-full lg:w-auto" />
          <CTAButton data={buttonTwo} className="w-full lg:w-auto" />
        </div>
      </div>
    </section>
  );
}
